<template>
    <div class="list-anestesi">
        <admin-lock-border />
        <div class="header">
            <h1>Anestesi</h1>
            <CustomButton type="button" variant="hollow" @click="openCreateAnestesi">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Skapa ny anestesi</span>
            </CustomButton>
        </div>

        <kliniken-table
            v-if="anestesiList.results"
            :items="anestesiList.results"
            paginationStyle="paging"
            :columns="columns"
            :count="count"
            :limit="limit"
            :currentPage="currentPage"
            @loadPage="loadPage"
            @changeLimit="changeLimit"
            @loadMore="loadMore"
            :showBottom="true"
            :showLimitDropdown="true"
            :has-dropdown="true"
            :dropdown-actions="dropdownActions"
            @editAnestesi="editAnestesiFromTable"
        />
    </div>
</template>

<script>
    import tabIcon from "@/assets/gear.svg";

    export default {
        name: "ListAnestesi",
        tabName: "Anestesi",
        icon: tabIcon,
    };
</script>

<script setup>
    import { onMounted, ref, onBeforeUnmount } from "vue";
    import emitter from "tiny-emitter/instance";
    import { openDialog } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import { getConfig, klinikenApi } from "@/api";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import CustomButton from "@/components/Button.vue";
    import Anestesi from "@/tabs/Admin/Anestesi.vue";
    import store from "@/store";

    const anestesiList = ref([]);
    const count = ref(0);
    const columns = ref([
        {
            title: "Namn",
            name: "displayName",
        },
        {
            title: "Blockad",
            name: "is_blockad",
            type: "boolean",
        },
        {
            title: "Huvudanestesikod",
            name: "huvudanestesikod_display.code",
        },
        {
            title: "Bianestesikoder antal",
            name: "bianestesikoder",
            getContent: (item) => {
                return item.bianestesikoder?.length ?? "";
            },
        },
        {
            title: "Ordningsnummer",
            name: "ordning",
        },
    ]);
    const dropdownActions = ref([
        {
            name: "editAnestesi",
            title: "Redigera anestesi",
            show: () => true,
        },
    ]);

    const limit = ref(50);
    const offset = ref(0);
    const fetchBatch = ref(1);
    const params = ref({});
    const currentPage = ref(1);

    const loadPage = async (page) => {
        currentPage.value = page;
        offset.value = (page - 1) * limit.value;
        await fetchAnestesiList();
    };

    const loadMore = async () => {
        fetchBatch.value++;
        await fetchAnestesiList();
    };

    const changeLimit = async (dropdownLimit) => {
        fetchBatch.value = 1;
        limit.value = dropdownLimit;
        await fetchAnestesiList();
    };

    const fetchAnestesiList = async () => {
        params.value = { ...params.value, limit: limit.value, offset: offset.value };
        if (limit.value > 0) {
            params.value["limit"] = limit.value * fetchBatch.value;
        }
        try {
            const response = await klinikenApi.get(
                "/operation/kodverk/anestesier/",
                getConfig({ params: { ...params.value } })
            );

            // Map huvudanestesikod id's to strings for vueformulate vueselect
            const responseDataResults = response.data.results.map((entry) => {
                entry.huvudanestesikod = entry.huvudanestesikod?.toString() ?? null;
                return entry;
            });

            anestesiList.value = {
                count: response.data.count,
                results: responseDataResults,
            };

            count.value = response.data.count;
        } catch (error) {
            openDialog("Kunde inte hämta OperationAnestesier.", "warning");
        }
    };
    const openCreateAnestesi = () => {
        store.dispatch("tabs/openTab", {
            component: Anestesi,
            parameters: { redigera: false },
        });
    };
    const editAnestesiFromTable = (anestesi) => {
        store.dispatch("tabs/openTab", {
            component: Anestesi,
            parameters: {
                redigera: true,
                ...anestesi,
            },
        });
    };

    onMounted(() => {
        fetchAnestesiList();
        emitter.on("Anestesi-table-uppdaterad", fetchAnestesiList);
    });

    onBeforeUnmount(() => {
        emitter.off("Anestesi-table-uppdaterad", fetchAnestesiList);
    });
</script>
<style lang="scss">
    .list-anestesi {
        .header {
            h1 {
                margin: 0;
            }

            display: flex;
            gap: 20px;
            align-items: center;
        }
    }
</style>
